// frontend/src/lib/parent-home-greeting.ts
// The companion-chip builder for the parent dashboard. Extracted from
// DashboardPage so it is unit-testable. Helper/circle branches were removed
// 2026-06-04 — the helper role is gated off and being removed platform-wide;
// tasks read as the family's own to-dos.
//
// `buildStevensGreeting` (the lede under the greeting) and `buildSummaryPills`
// (the count/next-appointment chips) were deleted 2026-09-05 (#1721): every
// line they wrote — "You have N appointments this week", "3 open to-dos",
// "Oncology follow-up · Monday 11:00 AM" — repeated a card lower on the page,
// and the fallback lede was filler. The H1 stands alone; the cards carry the
// facts once. The memorial register below still applies to the chips.
//
// ── The memorial register lives IN this module, not in its caller (#685/#693)
//
// Every builder here speaks in cheer and counts ("You have N appointments this
// week"), and two of the chips at buildChips() point straight at the care hub:
// "Log how your child is doing", "Log a medication". Offered to a bereaved
// family that is the platform asking a parent to log symptoms for a child who
// has died — and the chip lands on HealthMemorialView, which says in the same
// breath that the care record is closed.
//
// That never shipped, because #644 suppresses the whole companion rail in
// memorial mode at the CALLER (DashboardPage, locked by
// DashboardPage.lifecycle.test.tsx). That is the defect this wave is named
// after: a rule held by one surface and not by its neighbour. DashboardPage is
// the only consumer today, so the module was one new call site away from
// reintroducing the bug with nothing to catch it.
//
// So each builder now takes `isMemorial` and holds the rule itself. It is a
// required parameter on purpose: these are pure functions that cannot read the
// family record, so the only gate available is one the caller must supply, and
// a default would let the next consumer opt out by staying silent. A compile
// error is the gate. Callers pass `useDashboardMode()`'s `isMemorial` OR-ed
// with its `isLoading` — the fail-safe mode is 'active', so treatment copy
// must be held until the record lands rather than flashed and retracted.
//
// The caller-side #644 suppression stays exactly as it is. This is a second
// lock on the same door, not a replacement for the first.
import { parseISO, isToday } from 'date-fns';
import type { MedicalAppointment, Medication } from '@/types/api';
import { getTimeOfDay } from '@/lib/utils';
import { viewerCalendarDaysSince } from '@/lib/trfTime';
import { msg, type Msg } from '@/i18n/msg';

/**
 * One static chip. The label is a message descriptor (#2109 R3-B3): the page
 * resolves it at render, and the resolved label is also the message the chip
 * sends — so it is written in the UI language (R3-B2, ruled 2026-10-15).
 */
export interface ParentChip {
  label: Msg;
  href: string;
}

/**
 * The companion chips.
 *
 * Three of the four are treatment prompts pointing into the care hub, and all
 * three are withheld in the memorial register — an offer is the harm here, not
 * the destination. What survives is what a bereaved family still genuinely
 * has: the companion itself, and their own list. Both are real business the
 * #644 memo names explicitly, so this is a gate, not a blackout.
 *
 * `openTaskCount` is safe to keep because the caller already counts a filtered
 * list — DashboardPage passes `visibleParentTasks(rawTasks, mode)`, which drops
 * treatment-era items in memorial mode (#693). One classifier, one count: the
 * chip cannot promise a to-do the /tasks page then refuses to show.
 */
export function buildChips(
  nextAppt: MedicalAppointment | undefined,
  medications: Medication[],
  openTaskCount: number,
  companionName: string,
  isMemorial: boolean,
  now: Date = new Date(),
): ParentChip[] {
  const chips: ParentChip[] = [];

  chips.push({ label: msg('dashboard:chips.askCompanion', { companion: companionName }), href: '/chat' });

  if (!isMemorial) {
    const tod = getTimeOfDay();
    const isEvening = tod === 'evening';
    const hasApptToday = nextAppt && isToday(parseISO(nextAppt.date_time));

    if (nextAppt) {
      // #2837: calendar days on the viewer's clock, not 24-hour blocks. The
      // count floors at 0, so a past or same-day appointment still offers the
      // chip, as the negative count did before.
      const apptAt = new Date(nextAppt.date_time);
      const daysUntil = Number.isNaN(apptAt.getTime()) ? null : viewerCalendarDaysSince(now, apptAt);
      if (daysUntil !== null && daysUntil <= 2) {
        chips.push({
          label: nextAppt.provider
            ? msg('dashboard:chips.questionsFor', { provider: nextAppt.provider })
            : msg('dashboard:chips.questionsForDoctor'),
          href: '/medical?tab=questions',
        });
      }
    }

    const activeMeds = medications.filter((m) => m.active);
    if (isEvening && activeMeds.length > 0) {
      chips.push({ label: msg('dashboard:chips.logChild'), href: '/medical?tab=symptoms' });
    } else if (activeMeds.length > 0 && !hasApptToday) {
      chips.push({ label: msg('dashboard:chips.logChild'), href: '/medical?tab=symptoms' });
    } else if (activeMeds.length === 0) {
      chips.push({ label: msg('dashboard:chips.logMedication'), href: '/medical?tab=medications' });
    }
  }

  if (openTaskCount > 0) {
    chips.push({ label: msg('dashboard:chips.todosWaiting', { count: openTaskCount }), href: '/tasks' });
  } else {
    chips.push({ label: msg('dashboard:chips.visitCommunity'), href: '/community' });
  }

  return chips.slice(0, 4);
}
